import { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import LearnHub from "@/pages/LearnHub";
import ArticlePage from "@/pages/ArticlePage";
import BookPage from "@/pages/BookPage";
import ToolPage, { ToolsHub } from "@/pages/ToolPage";
import AdamSharePage from "@/pages/AdamSharePage";
import { SharePage } from "@/components/ShareWithGp";
import LoginPage, { AuthCallback, ProtectedRoute, ForgotPage, ResetPage } from "@/pages/portal/LoginPage";
import Dashboard, { AdminPage } from "@/pages/portal/Dashboard";
import { AuthProvider } from "@/lib/auth";
import ClinicalTeam from "@/pages/ClinicalTeam";
import WhyPage from "@/pages/WhyPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-ink px-6 text-center">
          <div>
            <p className="mono-label text-teal mb-4">Androgen Health</p>
            <h1 className="font-display text-4xl mb-3">Something went wrong</h1>
            <p className="text-mute">Please refresh the page to continue.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(hash);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
        else el.scrollIntoView({ behavior: "smooth" });
      }, 350);
      return () => clearTimeout(timer);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
}

function AppRoutes() {
  const location = useLocation(); // read hash here (reactive), not window.location.hash
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <div className="grain">
      <Nav />
      <main>
        <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/testosterone-treatment" element={<ServicePage slug="testosterone-treatment" />} />
                <Route path="/testosterone-testing" element={<ServicePage slug="testosterone-testing" />} />
                <Route path="/trt-cost" element={<ServicePage slug="trt-cost" />} />
                <Route path="/trt-eligibility" element={<ServicePage slug="trt-eligibility" />} />
                <Route path="/trt-treatment-options" element={<ServicePage slug="trt-treatment-options" />} />
                <Route path="/trt-monitoring" element={<ServicePage slug="trt-monitoring" />} />
                <Route path="/switch-trt-clinic" element={<ServicePage slug="switch-trt-clinic" />} />
                <Route path="/safety" element={<ServicePage slug="safety" />} />
                <Route path="/learn" element={<LearnHub />} />
                <Route path="/learn/:slug" element={<ArticlePage />} />
                <Route path="/adam-questionnaire" element={<ArticlePage slug="adam-questionnaire" />} />
                <Route path="/book" element={<BookPage />} />
                <Route path="/tools" element={<ToolsHub />} />
                <Route path="/tools/syringe-guide" element={<ToolPage slug="syringe-guide" />} />
                <Route path="/tools/unit-converter" element={<ToolPage slug="unit-converter" />} />
                <Route path="/tools/bmi-calculator" element={<ToolPage slug="bmi-calculator" />} />
                <Route path="/clinical-team" element={<ClinicalTeam />} />
                <Route path="/why-androgen-health" element={<WhyPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/adam/result/:id" element={<AdamSharePage />} />
          <Route path="/share/:token" element={<SharePage />} />
          <Route path="/portal/login" element={<LoginPage />} />
          <Route path="/portal/forgot" element={<ForgotPage />} />
          <Route path="/portal/reset" element={<ResetPage />} />
          <Route path="/portal" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/portal/admin" element={<ProtectedRoute admin><AdminPage /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SmoothScroll />
            <ScrollToTop />
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
